import { useRef, useCallback, useEffect } from "react";
import endCallUrl from "../sounds/end-call.ogg.js";
import toolCallUrl from "../sounds/tool-call.mp3.js";
const END_CALL_VOLUME = 0.6;
const TOOL_LOOP_VOLUME = 0.25;
const FADE_IN_MS = 200;
const FADE_OUT_MS = 350;
const RAMP_STEP_MS = 20;
function rampVolume(audio, from, to, durationMs, onComplete) {
  const steps = Math.max(1, Math.round(durationMs / RAMP_STEP_MS));
  const delta = (to - from) / steps;
  let current = from;
  let step = 0;
  audio.volume = from;
  const timer = setInterval(() => {
    step += 1;
    current += delta;
    if (step >= steps) {
      audio.volume = to;
      clearInterval(timer);
      onComplete == null ? void 0 : onComplete();
      return;
    }
    audio.volume = Math.max(0, Math.min(1, current));
  }, RAMP_STEP_MS);
  return timer;
}
function useVoiceSoundEffects() {
  const endCallAudioRef = useRef(null);
  const toolLoopAudioRef = useRef(null);
  const rampTimerRef = useRef(null);
  const loopRunningRef = useRef(false);
  const clearRamp = useCallback(() => {
    if (rampTimerRef.current != null) {
      clearInterval(rampTimerRef.current);
      rampTimerRef.current = null;
    }
  }, []);
  const getEndCallAudio = useCallback(() => {
    if (!endCallAudioRef.current) {
      const audio = new Audio(endCallUrl);
      audio.preload = "auto";
      endCallAudioRef.current = audio;
    }
    return endCallAudioRef.current;
  }, []);
  const getToolLoopAudio = useCallback(() => {
    if (!toolLoopAudioRef.current) {
      const audio = new Audio(toolCallUrl);
      audio.preload = "auto";
      audio.loop = true;
      toolLoopAudioRef.current = audio;
    }
    return toolLoopAudioRef.current;
  }, []);
  const playEndCall = useCallback(() => {
    clearRamp();
    const audio = getEndCallAudio();
    audio.currentTime = 0;
    audio.volume = END_CALL_VOLUME;
    void audio.play().catch(() => {
    });
  }, [clearRamp, getEndCallAudio]);
  const startToolLoop = useCallback(() => {
    if (loopRunningRef.current) return;
    loopRunningRef.current = true;
    clearRamp();
    const audio = getToolLoopAudio();
    audio.currentTime = 0;
    audio.volume = 0;
    void audio.play().catch(() => {
      loopRunningRef.current = false;
    });
    rampTimerRef.current = rampVolume(audio, 0, TOOL_LOOP_VOLUME, FADE_IN_MS, () => {
      rampTimerRef.current = null;
    });
  }, [clearRamp, getToolLoopAudio]);
  const stopToolLoop = useCallback(() => {
    if (!loopRunningRef.current && rampTimerRef.current == null) return;
    loopRunningRef.current = false;
    clearRamp();
    const audio = toolLoopAudioRef.current;
    if (!audio) return;
    const from = audio.volume;
    if (from <= 0) {
      audio.pause();
      audio.currentTime = 0;
      return;
    }
    rampTimerRef.current = rampVolume(audio, from, 0, FADE_OUT_MS, () => {
      rampTimerRef.current = null;
      audio.pause();
      audio.currentTime = 0;
    });
  }, [clearRamp]);
  useEffect(() => {
    return () => {
      var _a, _b;
      clearRamp();
      loopRunningRef.current = false;
      (_a = endCallAudioRef.current) == null ? void 0 : _a.pause();
      (_b = toolLoopAudioRef.current) == null ? void 0 : _b.pause();
    };
  }, [clearRamp]);
  return { playEndCall, startToolLoop, stopToolLoop };
}
export {
  useVoiceSoundEffects
};
//# sourceMappingURL=useVoiceSoundEffects.js.map
